import React, { useMemo, useState } from 'react'

import { every, get, isEmpty as lodashIsEmpty, map, pick } from 'lodash'
import styled from 'styled-components'

import Button from '../Button'
import Join from '../Join'
import Location from '../Location'
import Contact from './Contact'
import {
  getAddress,
  prefixAddress,
  relevantAddressFields,
  TAddressItem,
  unprefixAddress,
} from './helpers'

export interface TAddressProps extends TAddressItem {
  slim?: boolean
  labels?: string
  allowEdit?: boolean
  address?: TAddressItem
  alternativeAddress?: TAddressItem
  title?: string
  alternativeTitle?: string
  fieldPrefix?: string
  alwaysShow?: boolean
  additionalRows?: React.ReactNode
  onEdit?: (id: number, highlightedId?: number) => void
  oneliner?: boolean
  withoutNames?: boolean
}

const Wrapper = styled.div`
  border: 0px solid magenta;
`

const Address = (props: TAddressProps): React.ReactElement => {
  const {
    slim = false,
    labels,
    allowEdit,
    address,
    alternativeAddress,
    title,
    alternativeTitle,
    fieldPrefix,
    additionalRows,
    alwaysShow,
    oneliner,
    withoutNames,
  } = props

  const [isAlternative, setIsAlternative] = useState(false)

  const currentAddress = useMemo((): Partial<TAddressItem> => {
    if (!alternativeAddress || !isAlternative) {
      return getAddress(address || props, fieldPrefix as string)
    }
    return getAddress(alternativeAddress, fieldPrefix as string)
  }, [isAlternative, address, alternativeAddress, props])

  const toggleAddress = (): void => setIsAlternative(!isAlternative)

  const onEdit = (): void => {
    if (props.onEdit) {
      props.onEdit(currentAddress.id as number, currentAddress.highlightedId)
    }
  }

  const isEmpty = useMemo((): boolean => {
    return every(
      map(pick(currentAddress, relevantAddressFields), (prop) => {
        return !prop && prop !== ''
      }),
    )
  }, [currentAddress])

  const onelinerComponent = (
    <div>
      {[
        withoutNames ? null : currentAddress.name1,
        [
          currentAddress.street,
          currentAddress.streetNumber,
          currentAddress.streetNumberAddition,
        ]
          .filter((s) => s && s !== '')
          .join(' '),
        currentAddress.city,
        currentAddress.country && currentAddress.zip
          ? `${currentAddress.country}-${currentAddress.zip}`
          : null,
      ]
        .filter((s) => s && s !== '')
        .join(', ')}
    </div>
  )

  if (oneliner) {
    return onelinerComponent
  }

  return (
    <Wrapper>
      {title && (
        <div className="d-flex align-items-center">
          <h4 className="mb-1 mt-1">
            {isAlternative ? alternativeTitle : title}
          </h4>
          {alternativeAddress && (
            <Button
              xs
              info
              icon="fas fa-user-friends"
              tooltip={i18n.t(`show_title_alternate`, {
                defaultValue: `{{placeholder1}} anzeigen`,
                placeholder1: isAlternative ? title : alternativeTitle,
              })}
              className="ml-1 text-nowrap"
              onClick={toggleAddress}
            />
          )}
          {allowEdit && currentAddress.id && (
            <Button
              size="xs"
              variant="info"
              icon="fas fa-link"
              tooltip={i18n.t(
                `open_address_matching`,
                `Adresszuweisung in der Adressverwaltung öffnen`,
              )}
              className="ml-1 text-nowrap"
              onClick={onEdit}
            />
          )}
          {labels && <div className="ml-2">{labels}</div>}
        </div>
      )}
      {isEmpty && !alwaysShow ? (
        <div className="text-muted">
          {i18n.t('no_address', 'Keine Adresse')}
        </div>
      ) : (
        <table
          className={`borderless mb-0 table table-${slim ? 'nopadding' : 'xs'}`}
        >
          <tbody>
            {!withoutNames &&
              ((currentAddress.name1 && currentAddress.name1 !== '') ||
                (currentAddress.name2 && currentAddress.name2 !== '') ||
                (currentAddress.name3 && currentAddress.name3 !== '')) && (
                <tr>
                  {!slim && (
                    <td
                      width="1%"
                      className="text-nowrap text-right"
                    >
                      <div className="text-small text-muted">
                        {i18n.t('name', 'Name')}
                      </div>
                    </td>
                  )}
                  <td>
                    <strong>
                      <Join separator={<span>, </span>}>
                        {currentAddress.name1 &&
                          currentAddress.name1 !== '' && (
                            <span>{currentAddress.name1}</span>
                          )}
                        {currentAddress.name2 &&
                          currentAddress.name2 !== '' && (
                            <span>{currentAddress.name2}</span>
                          )}
                        {currentAddress.name3 &&
                          currentAddress.name3 !== '' && (
                            <span>{currentAddress.name3}</span>
                          )}
                      </Join>
                    </strong>
                  </td>
                </tr>
              )}
            {currentAddress.street && (
              <tr>
                {!slim && (
                  <td className="text-nowrap text-right">
                    <div className="text-small text-muted">
                      {i18n.t('address', 'Anschrift')}
                    </div>
                  </td>
                )}
                <td>
                  {[
                    currentAddress.street,
                    currentAddress.streetNumber,
                    currentAddress.streetNumberAddition,
                  ]
                    .filter((i) => !lodashIsEmpty(i))
                    .join(' ')}
                </td>
              </tr>
            )}
            {(currentAddress.country ||
              currentAddress.zip ||
              currentAddress.city) && (
              <tr>
                {!slim && (
                  <td className="text-nowrap text-right">
                    <div className="text-small text-muted">
                      {i18n.t('city', 'Ort')}
                    </div>
                  </td>
                )}
                <td>
                  {currentAddress.country}-{currentAddress.zip}{' '}
                  {currentAddress.city}
                </td>
              </tr>
            )}
            {currentAddress.lonlat &&
              Number(get(currentAddress.lonlat, 0, 0)) !== 0 && (
                <tr>
                  {!slim && (
                    <td>
                      <div className="text-small text-muted text-right">
                        {i18n.t('location', 'Position')}
                      </div>
                    </td>
                  )}
                  <td>
                    <Location
                      lat={get(currentAddress.lonlat, 1)}
                      lon={get(currentAddress.lonlat, 0)}
                      title={onelinerComponent}
                    >
                      <div>{onelinerComponent}</div>
                    </Location>
                  </td>
                </tr>
              )}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
              .map((i) => {
                const value = get(currentAddress, `ref${i}`)
                if (!value || lodashIsEmpty(value)) {
                  return
                }
                return (
                  <tr key={`row_ref_${i}`}>
                    {!slim && (
                      <td className="text-nowrap">
                        <div className="text-small text-muted text-right">
                          {i18n.t('reference', 'Referenz')} {i}
                        </div>
                      </td>
                    )}
                    <td>
                      <span>{get(currentAddress, `ref${i}`)}</span>
                    </td>
                  </tr>
                )
              })
              .filter(Boolean)}
            {additionalRows}
            <Contact
              address={currentAddress}
              slim={slim}
            />
          </tbody>
        </table>
      )}
    </Wrapper>
  )
}

export default Address

export { getAddress, unprefixAddress, prefixAddress }
